import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class CartUpsellCard extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this._handleChange = this.handleChange.bind(this);
    this._handleSubmit = this.handleSubmit.bind(this);

    this.addEventListener('change', this._handleChange);
    this.addEventListener('submit', this._handleSubmit);

    this.handleVariantChange();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._handleChange);
    this.removeEventListener('submit', this._handleSubmit);
  }

  get form() {
    return this.querySelector('form');
  }

  get variantSelect() {
    return this.querySelector('[data-upsell-variant-select]');
  }

  get quantityInput() {
    return this.querySelector('[data-upsell-quantity]');
  }

  get submitButton() {
    return this.querySelector('[data-upsell-submit]');
  }

  get errorElement() {
    return this.querySelector('[data-upsell-error]');
  }

  get selectedVariantSettings() {
    const option = this.variantSelect?.selectedOptions?.[0];
    if (!option?.dataset.variantSettings) return null;

    try {
      return JSON.parse(option.dataset.variantSettings);
    } catch (error) {
      console.error('Failed to parse cart upsell variant settings', error);
      return null;
    }
  }

  get sectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    return Array.from(sectionIds);
  }

  setBusy(isBusy) {
    if (!this.submitButton) return;

    this.submitButton.disabled = isBusy;
    this.submitButton.toggleAttribute('aria-busy', isBusy);
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.classList.remove('hidden');
  }

  clearError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = '';
    this.errorElement.classList.add('hidden');
  }

  handleChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-upsell-variant-select]')) return;

    this.handleVariantChange(event.target);
  }

  handleVariantChange(select = this.variantSelect) {
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions?.[0];
    if (!option?.dataset.variantSettings) return;

    let settings = null;
    try {
      settings = JSON.parse(option.dataset.variantSettings);
    } catch (error) {
      console.error('Failed to parse cart upsell variant settings', error);
      return;
    }

    if (!settings) return;

    const image = this.querySelector('[data-upsell-image]');
    const currentPrice = this.querySelector('[data-upsell-current-price]');
    const compareWrapper = this.querySelector('[data-upsell-compare-wrapper]');
    const comparePrice = this.querySelector('[data-upsell-compare-price]');

    this.querySelectorAll('[data-upsell-link]').forEach((link) => {
      link.setAttribute('href', settings.url);
    });

    if (image instanceof HTMLImageElement && settings.imageSrc) {
      image.src = settings.imageSrc;
      image.srcset = settings.imageSrc;
    }

    if (currentPrice) {
      currentPrice.textContent = settings.priceFormatted || currentPrice.textContent;
    }

    if (compareWrapper && comparePrice) {
      if (settings.comparePrice && settings.comparePrice > settings.price) {
        compareWrapper.hidden = false;
        comparePrice.textContent = settings.comparePriceFormatted || comparePrice.textContent;
      } else {
        compareWrapper.hidden = true;
      }
    }

    if (this.quantityInput && this.dataset.mode !== 'replace') {
      this.quantityInput.value = '1';
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  async addItem(quantity) {
    const form = this.form;
    if (!form) throw new Error('Upsell form not found');

    const formData = new FormData(form);
    const sectionIds = this.sectionIds;

    formData.set('id', this.variantSelect?.value || formData.get('id') || '');
    formData.set('quantity', String(quantity));

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });

    const json = await response.json();
    if (json.status) {
      throw new Error(json.description || json.message || 'Unable to add item to cart');
    }

    return json;
  }

  async changeLine(line, quantity) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const responseText = await response.text();
    const json = JSON.parse(responseText);

    if (json.errors) {
      throw new Error(json.errors);
    }

    return json;
  }

  canReplace(targetQuantity, variantSettings) {
    return Boolean(
      this.dataset.mode === 'replace' &&
        this.dataset.triggerLine &&
        (variantSettings.continueSelling || Number(variantSettings.quantity) >= Number(targetQuantity))
    );
  }

  async handleSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (event.target !== this.form) return;

    event.preventDefault();
    this.clearError();
    this.setBusy(true);

    const variantSettings = this.selectedVariantSettings;
    if (!variantSettings) {
      this.setBusy(false);
      return;
    }

    const triggerLine = Number(this.dataset.triggerLine || 0);
    const triggerQuantity = Number(this.dataset.triggerQuantity || 1);

    let finalQuantity = 1;
    let sectionsResponse;

    try {
      if (this.canReplace(triggerQuantity, variantSettings)) {
        finalQuantity = triggerQuantity;
        const addResponse = await this.addItem(finalQuantity);

        try {
          sectionsResponse = await this.changeLine(triggerLine, 0);
        } catch (error) {
          console.error(error);
          sectionsResponse = addResponse;
          this.showError('Added offer product, but could not remove original item.');
        }
      } else {
        sectionsResponse = await this.addItem(1);
      }

      const cart = await this.fetchCart();

      document.dispatchEvent(
        new CartAddEvent(cart, String(variantSettings.id), {
          source: 'cart-upsell-card',
          itemCount: cart?.item_count ?? finalQuantity,
          productId: this.dataset.offerProductId,
          sections: sectionsResponse.sections,
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add item to cart';
      console.error(error);
      this.showError(message);
      document.dispatchEvent(new CartErrorEvent(this.dataset.offerProductId || '', message, message, {}));
    } finally {
      this.setBusy(false);
    }
  }
}

if (!customElements.get('cart-upsell-card')) {
  customElements.define('cart-upsell-card', CartUpsellCard);
}
